import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Avatar,
  Button,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Snackbar,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider
} from '@mui/material';
import {
  LocalBar as BarIcon,
  Person as PersonIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckIcon,
  ArrowBack as BackIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Undo as UndoIcon
} from '@mui/icons-material';
import { barService } from '../services/bar';
import { resolveMediaUrl } from '../utils/mediaUrl';

const AdminBar = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [drinks, setDrinks] = useState([]);
  const [activeEvent, setActiveEvent] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [addingTransaction, setAddingTransaction] = useState(false);
  
  // New state for improved UX
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedDrinks, setSelectedDrinks] = useState({}); // { drinkId: quantity }
  const [userTransactions, setUserTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);

  useEffect(() => {
    loadBarData();
  }, []);

  const loadBarData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Load bar config to get active event
      const configResult = await barService.getBarConfig();
      if (!configResult.success || !configResult.data?.active_event_id) {
        setError('No active event configured. Please configure an event in the admin panel.');
        setLoading(false);
        return;
      }

      setActiveEvent(configResult.data);

      // Load participants and drinks
      const [participantsResult, drinksResult] = await Promise.all([
        barService.getActiveEventParticipants(),
        barService.getDrinks()
      ]);

      if (participantsResult.success) {
        setParticipants(participantsResult.data);
      } else {
        setError(participantsResult.error);
      }

      if (drinksResult.success) {
        // Filter only available drinks
        setDrinks(drinksResult.data.filter(d => d.available));
      } else {
        setError(drinksResult.error);
      }
    } catch (err) {
      setError('Failed to load bar data');
      console.error('Bar data loading error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectUser = async (user) => {
    setSelectedUser(user);
    setSelectedDrinks({});
    
    // Load user's transactions for this event
    if (activeEvent?.active_event_id) {
      setLoadingTransactions(true);
      const result = await barService.getUserTransactions(user.user_id, activeEvent.active_event_id);
      if (result.success) {
        setUserTransactions(result.data);
      }
      setLoadingTransactions(false);
    }
  };

  const handleBackToUsers = () => {
    setSelectedUser(null);
    setSelectedDrinks({});
    setUserTransactions([]);
  };

  const handleDrinkQuantityChange = (drinkId, change) => {
    setSelectedDrinks(prev => {
      const current = prev[drinkId] || 0;
      const newQuantity = Math.max(0, current + change);
      
      if (newQuantity === 0) {
        const { [drinkId]: removed, ...rest } = prev;
        return rest;
      }
      
      return { ...prev, [drinkId]: newQuantity };
    });
  };

  const handleSubmitDrinks = async () => {
    if (Object.keys(selectedDrinks).length === 0 || addingTransaction) return;

    setAddingTransaction(true);
    let successCount = 0;
    let totalAmount = 0;

    // Add each selected drink as a transaction
    for (const [drinkId, quantity] of Object.entries(selectedDrinks)) {
      const result = await barService.addTransaction(selectedUser.user_id, drinkId, quantity);
      if (result.success) {
        successCount++;
        totalAmount += result.data.price * result.data.quantity;
      }
    }

    if (successCount > 0) {
      // Update participant's total locally
      setParticipants(prevParticipants =>
        prevParticipants.map(p =>
          p.user_id === selectedUser.user_id
            ? { ...p, total_spent: p.total_spent + totalAmount }
            : p
        )
      );

      // Update selected user total
      setSelectedUser(prev => ({
        ...prev,
        total_spent: prev.total_spent + totalAmount
      }));

      // Reload transactions
      const result = await barService.getUserTransactions(selectedUser.user_id, activeEvent.active_event_id);
      if (result.success) {
        setUserTransactions(result.data);
      }

      setSnackbar({
        open: true,
        message: `${successCount} drink(s) added successfully!`
      });

      setSelectedDrinks({});
    } else {
      setSnackbar({
        open: true,
        message: 'Failed to add drinks'
      });
    }

    setAddingTransaction(false);
  };

  const handleUndoTransaction = async (transactionId) => {
    const result = await barService.deleteTransaction(transactionId);
    
    if (result.success) {
      // Find the transaction to calculate the refund amount
      const transaction = userTransactions.find(t => t._id === transactionId);
      if (transaction) {
        const refundAmount = transaction.price * transaction.quantity;
        
        // Update participant's total
        setParticipants(prevParticipants =>
          prevParticipants.map(p =>
            p.user_id === selectedUser.user_id
              ? { ...p, total_spent: p.total_spent - refundAmount }
              : p
          )
        );

        // Update selected user total
        setSelectedUser(prev => ({
          ...prev,
          total_spent: prev.total_spent - refundAmount
        }));

        // Remove from transactions list
        setUserTransactions(prev => prev.filter(t => t._id !== transactionId));

        setSnackbar({
          open: true,
          message: 'Transaction removed successfully'
        });
      }
    } else {
      setSnackbar({
        open: true,
        message: result.error || 'Failed to remove transaction'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getTotalRevenue = () => {
    return participants.reduce((sum, p) => sum + (p.total_spent || 0), 0);
  };

  const getSelectedTotal = () => {
    return Object.entries(selectedDrinks).reduce((sum, [drinkId, quantity]) => {
      const drink = drinks.find(d => d._id === drinkId);
      return sum + (drink ? drink.price * quantity : 0);
    }, 0);
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading bar...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={loadBarData}
        >
          Retry
        </Button>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: 4 }}>
      {/* Header */}
      <Paper
        elevation={3}
        sx={{
          p: 3,
          mb: 3,
          position: 'sticky',
          top: 0,
          zIndex: 100,
          bgcolor: 'primary.main',
          color: 'white'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {selectedUser && (
              <IconButton
                onClick={handleBackToUsers}
                sx={{ color: 'white' }}
                size="large"
              >
                <BackIcon fontSize="large" />
              </IconButton>
            )}
            <BarIcon sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {selectedUser ? selectedUser.username : 'Bar - Select User'}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                {selectedUser ? `${selectedUser.total_spent.toFixed(2)} CHF` : 'Active Event'}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              {getTotalRevenue().toFixed(2)} CHF
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Total Revenue
            </Typography>
          </Box>
          <IconButton
            onClick={loadBarData}
            sx={{ color: 'white' }}
            size="large"
          >
            <RefreshIcon fontSize="large" />
          </IconButton>
        </Box>
      </Paper>

      <Container maxWidth="xl">
        {!selectedUser ? (
          /* User Selection View */
          <>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
              Select a participant:
            </Typography>
            {participants.length === 0 ? (
              <Alert severity="info">
                No participants found for the active event.
              </Alert>
            ) : (
              <Grid container spacing={3}>
                {participants.map((participant) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={participant.user_id}>
                    <Card
                      elevation={3}
                      sx={{
                        height: '100%',
                        cursor: 'pointer',
                        '&:hover': {
                          transition: 'transform 0.2s ease-in-out',
                          boxShadow: 6
                        }
                      }}
                      onClick={() => handleSelectUser(participant)}
                    >
                      <CardContent sx={{ textAlign: 'center', p: 3 }}>
                        <Avatar
                          src={resolveMediaUrl(participant.avatar)}
                          alt={participant.username}
                          sx={{
                            width: 100,
                            height: 100,
                            mx: 'auto',
                            mb: 2,
                            bgcolor: 'primary.main',
                            fontSize: '2.5rem'
                          }}
                        >
                          {!participant.avatar && <PersonIcon fontSize="large" />}
                        </Avatar>
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 'bold',
                            mb: 2
                          }}
                        >
                          {participant.username}
                        </Typography>
                        <Chip
                          label={`${(participant.total_spent || 0).toFixed(2)} CHF`}
                          color="success"
                          sx={{
                            fontWeight: 'bold',
                            fontSize: '1.2rem',
                            padding: '24px 16px'
                          }}
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </>
        ) : (
          /* Drink Selection View */
          <Grid container spacing={3}>
            {/* Left: Drink Selection */}
            <Grid item xs={12} md={7}>
              <Paper elevation={2} sx={{ p: 3 }}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
                  Select Drinks:
                </Typography>
                <Grid container spacing={2}>
                  {drinks.map((drink) => {
                    const quantity = selectedDrinks[drink._id] || 0;
                    return (
                      <Grid item xs={12} sm={6} key={drink._id}>
                        <Card
                          elevation={quantity > 0 ? 4 : 1}
                          sx={{
                            border: quantity > 0 ? 2 : 0,
                            borderColor: 'primary.main'
                          }}
                        >
                          <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                              <Box>
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                  {drink.name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {drink.category}
                                </Typography>
                              </Box>
                              <Typography variant="h6" color="primary.main" sx={{ fontWeight: 'bold' }}>
                                {drink.price.toFixed(2)} CHF
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                              <IconButton
                                color="primary"
                                size="large"
                                onClick={() => handleDrinkQuantityChange(drink._id, -1)}
                                disabled={quantity === 0}
                              >
                                <RemoveIcon fontSize="large" />
                              </IconButton>
                              <Typography variant="h4" sx={{ minWidth: 60, textAlign: 'center', fontWeight: 'bold' }}>
                                {quantity}
                              </Typography>
                              <IconButton
                                color="primary"
                                size="large"
                                onClick={() => handleDrinkQuantityChange(drink._id, 1)}
                              >
                                <AddIcon fontSize="large" />
                              </IconButton>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>

                {/* Submit Button */}
                <Box sx={{ mt: 4 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    onClick={handleSubmitDrinks}
                    disabled={Object.keys(selectedDrinks).length === 0 || addingTransaction}
                    sx={{
                      py: 2,
                      fontSize: '1.5rem',
                      fontWeight: 'bold'
                    }}
                  >
                    {addingTransaction ? (
                      <CircularProgress size={30} color="inherit" />
                    ) : (
                      `Add Drinks - ${getSelectedTotal().toFixed(2)} CHF`
                    )}
                  </Button>
                </Box>
              </Paper>
            </Grid>

            {/* Right: Transaction History */}
            <Grid item xs={12} md={5}>
              <Paper elevation={2} sx={{ p: 3 }}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
                  Recent Transactions:
                </Typography>
                {loadingTransactions ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : userTransactions.length === 0 ? (
                  <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    No transactions yet
                  </Typography>
                ) : (
                  <List>
                    {userTransactions.map((transaction, index) => (
                      <React.Fragment key={transaction._id}>
                        {index > 0 && <Divider />}
                        <ListItem>
                          <ListItemText
                            primary={
                              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                {transaction.quantity}x {transaction.drink_name}
                              </Typography>
                            }
                            secondary={
                              <>
                                <Typography variant="body2" color="text.secondary">
                                  {new Date(transaction.timestamp).toLocaleTimeString()} - {(transaction.price * transaction.quantity).toFixed(2)} CHF
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Added by: {transaction.added_by_username}
                                </Typography>
                              </>
                            }
                          />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              color="error"
                              onClick={() => handleUndoTransaction(transaction._id)}
                              title="Undo transaction"
                            >
                              <UndoIcon />
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </Paper>
            </Grid>
          </Grid>
        )}
      </Container>

      {/* Success Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="success"
          sx={{
            width: '100%',
            fontSize: '1.1rem',
            '& .MuiAlert-icon': {
              fontSize: '2rem'
            }
          }}
          icon={<CheckIcon />}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminBar;
